import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../stores/appStore';
import {
  Card,
  CardContent,
  Badge,
  Button,
  Input,
} from '../components/ui';
import {
  Link2,
  RefreshCw,
  ExternalLink,
  AlertCircle,
  CheckCircle,
  Clock,
  Bot,
  Users,
  Unlink,
  ArrowLeft,
  Eye,
  Settings,
  ChevronDown,
  CreditCard,
  Mail,
  SlidersHorizontal,
  XCircle,
} from 'lucide-react';
import { motion } from 'framer-motion';
import type { BitrixConnection, PartnerClientSummary } from '../types';
import { api } from '../lib/api';
import { toast } from '../stores/toastStore';
import { enablePartnerReadOnlyMode, disablePartnerReadOnlyMode } from '../lib/readOnlyMode';
import { useTranslation } from 'react-i18next';

type ClientMenuAction = {
  key: string;
  divider?: boolean;
  label?: string;
  icon?: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  title?: string;
  danger?: boolean;
};

/**
 * Single "Gerenciar" button that opens a dropdown with every per-client action.
 * Replaces the previous row of 6 inline buttons for a cleaner partner layout.
 */
function ClientActionsMenu({ label, actions }: { label: string; actions: ClientMenuAction[] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <Button size="sm" onClick={() => setOpen((o) => !o)} className="gap-1.5">
        <Settings className="w-4 h-4" />
        {label}
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
      </Button>
      {open && (
        <div className="absolute right-0 mt-2 w-64 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg z-30 py-1 overflow-hidden">
          {actions.map((action) =>
            action.divider ? (
              <div key={action.key} className="my-1 border-t border-slate-100 dark:border-slate-700" />
            ) : (
              <button
                key={action.key}
                type="button"
                disabled={action.disabled}
                title={action.title}
                onClick={() => {
                  setOpen(false);
                  action.onClick?.();
                }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                  action.danger
                    ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10'
                    : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700/60'
                }`}
              >
                <span className="shrink-0">{action.icon}</span>
                <span className="flex-1">{action.label}</span>
              </button>
            ),
          )}
        </div>
      )}
    </div>
  );
}

export function Connections() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { tenant, connections, reconnectConnection, fetchAll, isLoading } =
    useAppStore();
  const [reconnecting, setReconnecting] = useState<string | null>(null);
  const [partnerClients, setPartnerClients] = useState<PartnerClientSummary[]>([]);
  const [loadingPartnerClients, setLoadingPartnerClients] = useState(false);
  const [linkDomain, setLinkDomain] = useState('');
  const [linkingClient, setLinkingClient] = useState(false);
  const [unlinkingClientId, setUnlinkingClientId] = useState<string | null>(null);
  const [cancellingLicenseClientId, setCancellingLicenseClientId] = useState<string | null>(null);
  const [managingPaymentClientId, setManagingPaymentClientId] = useState<string | null>(null);
  const [contractingLicenseClientId, setContractingLicenseClientId] = useState<string | null>(null);
  const [updatingContactClientId, setUpdatingContactClientId] = useState<string | null>(null);
  const [licenseModalClient, setLicenseModalClient] = useState<PartnerClientSummary | null>(null);
  const [licenseModalPlanCode, setLicenseModalPlanCode] = useState('');
  const [openingClientId, setOpeningClientId] = useState<string | null>(null);
  const [partnerError, setPartnerError] = useState<string | null>(null);
  const [partnerTenantId] = useState<string | null>(() => localStorage.getItem('partnerTenantId'));
  const [actingTenantId] = useState<string | null>(() => localStorage.getItem('actingTenantId'));
  const [billingPlans, setBillingPlans] = useState<Array<{
    code: string;
    name: string;
    bots: number;
    monthlyPriceUsd: number;
    stripePriceId: string;
    recommended: boolean;
    isAddon: boolean;
  }>>([]);
  const [managePlanClient, setManagePlanClient] = useState<PartnerClientSummary | null>(null);
  const [managePlanBotLimit, setManagePlanBotLimit] = useState(1);
  const [managePlanStatus, setManagePlanStatus] = useState<'active' | 'suspended'>('active');
  const [savingPlan, setSavingPlan] = useState(false);
  const currentLocale = i18n.language.toLowerCase().startsWith('pt') ? 'pt-BR' : 'en-US';

  // Data is fetched in App.tsx

  const loadPartnerClients = async () => {
    if (!tenant?.id || !tenant?.isPartner) {
      return;
    }

    setLoadingPartnerClients(true);
    setPartnerError(null);

    try {
      const data = await api.getPartnerClients(tenant.id);
      setPartnerClients(data);
    } catch (error: any) {
      setPartnerError(error?.message || t('connections.errors.loadPartnerClients'));
    } finally {
      setLoadingPartnerClients(false);
    }
  };

  useEffect(() => {
    void loadPartnerClients();
  }, [tenant?.id, tenant?.isPartner]);

  useEffect(() => {
    const loadBillingPlans = async () => {
      if (!tenant?.isPartner) {
        setBillingPlans([]);
        return;
      }

      try {
        const config = await api.getBillingConfig();
        setBillingPlans(config.plans || []);
      } catch {
        setBillingPlans([]);
      }
    };

    void loadBillingPlans();
  }, [tenant?.isPartner]);

  useEffect(() => {
    if (tenant?.isPartner && tenant.id) {
      localStorage.setItem('partnerTenantId', tenant.id);
      localStorage.removeItem('actingTenantId');
      disablePartnerReadOnlyMode();
    }
  }, [tenant?.id, tenant?.isPartner]);


  const handleReconnect = async (id: string) => {
    setReconnecting(id);
    try {
      await reconnectConnection(id);
      toast.success(t('connections.toasts.reconnectSuccess'));
      await loadPartnerClients();
    } catch (error: any) {
      toast.error(error?.message || t('connections.errors.reconnectFailed'));
    } finally {
      setReconnecting(null);
    }
  };

  const getStatusBadge = (status: BitrixConnection['status']) => {
    const config = {
      active: { variant: 'success' as const, label: t('connections.status.active'), icon: CheckCircle },
      pending: { variant: 'warning' as const, label: t('connections.status.pending'), icon: Clock },
      error: { variant: 'danger' as const, label: t('connections.status.error'), icon: AlertCircle },
      disconnected: { variant: 'default' as const, label: t('connections.status.disconnected'), icon: Link2 },
    };
    const { variant, label, icon: Icon } = config[status];
    return (
      <Badge variant={variant} className="flex items-center gap-1">
        <Icon className="w-3 h-3" />
        {label}
      </Badge>
    );
  };

  const isOAuthRelatedError = (connection: BitrixConnection): boolean => {
    const raw = `${connection.lastError || ''}`.toLowerCase();
    return (
      connection.status === 'error' ||
      raw.includes('expired_token') ||
      raw.includes('invalid_grant') ||
      raw.includes('oauth') ||
      raw.includes('token') ||
      connection.status === 'disconnected'
    );
  };

  const getRenewAppUrl = (portalDomain: string): string => {
    return `https://${portalDomain}/marketplace/`;
  };

  const getTenantStatusBadge = (status: PartnerClientSummary['status']) => {
    const config = {
      active: { variant: 'success' as const, label: t('connections.status.active') },
      trial: { variant: 'warning' as const, label: t('connections.status.trial') },
      expired: { variant: 'danger' as const, label: t('connections.status.expired') },
      suspended: { variant: 'danger' as const, label: t('connections.status.suspended') },
      cancelled: { variant: 'default' as const, label: t('connections.status.cancelled') },
    };

    const item = config[status] || { variant: 'default' as const, label: status };
    return <Badge variant={item.variant}>{item.label}</Badge>;
  };

  const hasCancelableLicense = (stripeSubscriptionStatus?: string | null): boolean => {
    if (!stripeSubscriptionStatus) {
      return false;
    }

    return ['active', 'trialing', 'past_due', 'unpaid'].includes(
      stripeSubscriptionStatus.toLowerCase(),
    );
  };

  const handleLinkClient = async () => {
    if (!tenant?.id || !linkDomain.trim()) {
      return;
    }

    setLinkingClient(true);
    setPartnerError(null);

    try {
      const result = await api.linkPartnerClientByDomain(tenant.id, linkDomain.trim()) as any;
      setLinkDomain('');
      toast.success(result?.message || t('connections.toasts.linkClientSuccess'));
      await loadPartnerClients();
    } catch (error: any) {
      setPartnerError(error?.message || t('connections.errors.linkClientFailed'));
    } finally {
      setLinkingClient(false);
    }
  };

  const handleUnlinkClient = async (clientTenantId: string) => {
    if (!tenant?.id) {
      return;
    }

    setUnlinkingClientId(clientTenantId);
    setPartnerError(null);

    try {
      await api.unlinkPartnerClient(tenant.id, clientTenantId);
      await loadPartnerClients();
    } catch (error: any) {
      setPartnerError(error?.message || t('connections.errors.unlinkClientFailed'));
    } finally {
      setUnlinkingClientId(null);
    }
  };

  const handleCancelLicense = async (clientTenantId: string) => {
    if (!tenant?.id) {
      return;
    }

    setCancellingLicenseClientId(clientTenantId);
    setPartnerError(null);

    try {
      const session = await api.createStripePortalSessionForTarget(tenant.id, clientTenantId);
      const stripeWindow = window.open(session.url, '_blank', 'noopener,noreferrer');
      if (!stripeWindow) {
        window.prompt(t('connections.prompts.blockedNewTabManage'), session.url);
      }
    } catch (error: any) {
      const message = error?.message || t('connections.errors.openLicensePortalFailed');
      setPartnerError(message);
      toast.error(message);
    } finally {
      setCancellingLicenseClientId(null);
    }
  };

  // Opens the Stripe Billing Portal for the client — where the payment method,
  // invoices and subscription can be managed/updated.
  const handleManagePayment = async (clientTenantId: string) => {
    if (!tenant?.id) {
      return;
    }

    setManagingPaymentClientId(clientTenantId);
    setPartnerError(null);

    try {
      const session = await api.createStripePortalSessionForTarget(tenant.id, clientTenantId);
      const stripeWindow = window.open(session.url, '_blank', 'noopener,noreferrer');
      if (!stripeWindow) {
        window.prompt(t('connections.prompts.blockedNewTabManage'), session.url);
      }
    } catch (error: any) {
      const message = error?.message || t('connections.errors.openLicensePortalFailed');
      setPartnerError(message);
      toast.error(message);
    } finally {
      setManagingPaymentClientId(null);
    }
  };

  const getAvailablePlansForClient = (client: PartnerClientSummary) => {
    if (billingPlans.length === 0) {
      return [] as typeof billingPlans;
    }

    const hasTwoOrMoreBots = Number(client.botLimit || 0) >= 2;
    if (hasTwoOrMoreBots) {
      const addonPlans = billingPlans.filter((plan) => plan.isAddon || plan.code === 'bot_additional');
      return addonPlans;
    }

    return billingPlans.filter((plan) => !plan.isAddon);
  };

  const getDefaultPlanCodeForClient = (client: PartnerClientSummary): string | null => {
    const availablePlans = getAvailablePlansForClient(client);
    if (availablePlans.length === 0) {
      return null;
    }

    const byCurrentPlanCode = availablePlans.find((plan) => plan.code === client.subscriptionPlan);
    if (byCurrentPlanCode) {
      return byCurrentPlanCode.code;
    }

    const byBotLimit = client.botLimit
      ? availablePlans.find((plan) => plan.bots === client.botLimit)
      : null;

    if (byBotLimit) {
      return byBotLimit.code;
    }

    return availablePlans.find((plan) => plan.recommended)?.code || availablePlans[0].code;
  };

  const getSelectedPlanPriceIdForClient = (client: PartnerClientSummary, selectedPlanCode?: string): string | null => {
    const availablePlans = getAvailablePlansForClient(client);
    if (availablePlans.length === 0) {
      return null;
    }

    const effectivePlanCode = selectedPlanCode || getDefaultPlanCodeForClient(client);
    if (!effectivePlanCode) {
      return null;
    }

    return availablePlans.find((plan) => plan.code === effectivePlanCode)?.stripePriceId || null;
  };

  const handleContractLicense = async (client: PartnerClientSummary, selectedPlanCode?: string) => {
    if (!tenant?.id) {
      return;
    }

    setContractingLicenseClientId(client.id);
    setPartnerError(null);

    try {
      const priceId = getSelectedPlanPriceIdForClient(client, selectedPlanCode);
      if (!priceId) {
        throw new Error(t('connections.errors.noPlanAvailable'));
      }

      const session = await api.createStripeCheckoutSession(tenant.id, priceId, undefined, client.id);
      const stripeWindow = window.open(session.url, '_blank', 'noopener,noreferrer');
      if (!stripeWindow) {
        window.prompt(t('connections.prompts.blockedNewTabContract'), session.url);
      }
    } catch (error: any) {
      const message = error?.message || t('connections.errors.contractLicenseFailed');
      setPartnerError(message);
      toast.error(message);
    } finally {
      setContractingLicenseClientId(null);
    }
  };

  const openLicensePlanModal = (client: PartnerClientSummary) => {
    const availablePlans = getAvailablePlansForClient(client);
    if (availablePlans.length === 0) {
      toast.error(t('connections.errors.noPlanAvailable'));
      return;
    }

    const defaultPlanCode = getDefaultPlanCodeForClient(client) || availablePlans[0].code;
    setLicenseModalPlanCode(defaultPlanCode);
    setLicenseModalClient(client);
  };

  const closeLicensePlanModal = () => {
    if (contractingLicenseClientId) {
      return;
    }
    setLicenseModalClient(null);
    setLicenseModalPlanCode('');
  };

  const confirmLicensePlanModal = async () => {
    if (!licenseModalClient || !licenseModalPlanCode) {
      return;
    }

    await handleContractLicense(licenseModalClient, licenseModalPlanCode);
    setLicenseModalClient(null);
    setLicenseModalPlanCode('');
  };

  const handleOpenClientReadOnly = async (clientTenantId: string) => {
    if (!tenant?.id) {
      return;
    }

    setOpeningClientId(clientTenantId);
    try {
      localStorage.setItem('partnerTenantId', tenant.id);
      localStorage.setItem('actingTenantId', clientTenantId);
      localStorage.setItem('tenantId', clientTenantId);
      enablePartnerReadOnlyMode();
      await fetchAll(clientTenantId);
      navigate('/connections');
      toast.success(t('connections.toasts.readOnlyEnabled'));
    } catch (error: any) {
      toast.error(error?.message || t('connections.errors.openClientReadOnlyFailed'));
    } finally {
      setOpeningClientId(null);
    }
  };

  const handleUpdateClientContactEmail = async (client: PartnerClientSummary) => {
    if (!tenant?.id) {
      return;
    }

    const currentContact = (client.technicalResponsibleEmail || client.email || '').trim();
    const nextContact = window.prompt(t('connections.partner.contactPrompt'), currentContact);
    if (nextContact === null) {
      return;
    }

    const normalized = nextContact.trim();
    if (!normalized) {
      toast.error(t('connections.partner.contactEmptyError'));
      return;
    }

    setUpdatingContactClientId(client.id);
    setPartnerError(null);
    try {
      await api.updatePartnerClientContactEmail(tenant.id, client.id, normalized);
      toast.success(t('connections.partner.contactUpdated'));
      await loadPartnerClients();
    } catch (error: any) {
      const message = error?.message || t('connections.partner.contactUpdateFailed');
      setPartnerError(message);
      toast.error(message);
    } finally {
      setUpdatingContactClientId(null);
    }
  };

  const handleBackToPartner = async () => {
    if (!partnerTenantId) {
      return;
    }

    localStorage.setItem('tenantId', partnerTenantId);
    localStorage.removeItem('actingTenantId');
    disablePartnerReadOnlyMode();

    try {
      await fetchAll(partnerTenantId);
      navigate('/connections');
      toast.success(t('connections.toasts.backToPartnerSuccess'));
    } catch (error: any) {
      toast.error(error?.message || t('connections.errors.backToPartnerFailed'));
    }
  };

  const isPartnerActingAsClient =
    !!partnerTenantId && !!actingTenantId && (!tenant?.isPartner || tenant.id !== partnerTenantId);

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="space-y-2">
          <div className="h-8 w-56 bg-slate-200 dark:bg-slate-700 rounded" />
          <div className="h-4 w-80 bg-slate-200 dark:bg-slate-700 rounded" />
        </div>

        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 bg-slate-200 dark:bg-slate-700 rounded-lg" />
            <div className="space-y-2 flex-1">
              <div className="h-5 w-32 bg-slate-200 dark:bg-slate-700 rounded" />
              <div className="h-4 w-full max-w-2xl bg-slate-200 dark:bg-slate-700 rounded" />
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 space-y-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 bg-slate-200 dark:bg-slate-700 rounded-lg" />
                  <div className="space-y-2">
                    <div className="h-5 w-52 bg-slate-200 dark:bg-slate-700 rounded" />
                    <div className="h-4 w-40 bg-slate-200 dark:bg-slate-700 rounded" />
                    <div className="h-4 w-56 bg-slate-200 dark:bg-slate-700 rounded" />
                  </div>
                </div>
                <div className="h-8 w-20 bg-slate-200 dark:bg-slate-700 rounded" />
              </div>
              <div className="pt-4 border-t border-slate-100 dark:border-slate-700">
                <div className="h-4 w-36 bg-slate-200 dark:bg-slate-700 rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (tenant?.isPartner) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.24, ease: 'easeOut' }}
        className="space-y-6"
      >
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{t('connections.partner.title')}</h1>
          <p className="text-slate-500 dark:text-slate-300 mt-1">
            {t('connections.partner.subtitle')}
          </p>
        </div>

        <Card className="bg-blue-50 dark:bg-blue-500/15 border-blue-200 dark:border-blue-500/30">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-500/20 rounded-lg">
                <Users className="w-5 h-5 text-blue-600 dark:text-blue-300" />
              </div>
              <div>
                <p className="font-medium text-blue-900 dark:text-blue-200">{t('connections.partner.panelTitle')}</p>
                <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                  {t('connections.partner.panelDescription')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-3 items-end">
              <Input
                label={t('connections.partner.domainLabel')}
                placeholder={t('connections.partner.domainPlaceholder')}
                value={linkDomain}
                onChange={(event) => setLinkDomain(event.target.value)}
              />
              <Button onClick={handleLinkClient} disabled={linkingClient || !linkDomain.trim()}>
                {linkingClient ? t('connections.partner.linking') : t('connections.partner.linkClient')}
              </Button>
            </div>
            {partnerError && (
              <p className="text-sm text-red-600 mt-3">{partnerError}</p>
            )}
          </CardContent>
        </Card>

        {loadingPartnerClients ? (
          <Card>
            <CardContent className="py-8 text-center text-slate-500 dark:text-slate-300">
              {t('connections.partner.loadingClients')}
            </CardContent>
          </Card>
        ) : partnerClients.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center">
              <Users className="w-14 h-14 text-slate-300 dark:text-slate-500 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-1">
                {t('connections.partner.noClientsTitle')}
              </h3>
              <p className="text-slate-500 dark:text-slate-300 text-sm">
                {t('connections.partner.noClientsDescription')}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {partnerClients.map((client, index) => (
              <motion.div
                key={client.id}
                initial={{ opacity: 0, y: 10, scale: 0.99 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.22, delay: index * 0.03, ease: 'easeOut' }}
              >
                <Card>
                  <CardContent className="p-5">
                    {(() => {
                      const canCancelLicense = hasCancelableLicense(client.stripeSubscriptionStatus);
                      return (
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-slate-900 dark:text-slate-100">{client.name}</h3>
                          {getTenantStatusBadge(client.status)}
                        </div>
                        <p className="text-sm text-slate-500 dark:text-slate-300">{client.email}</p>
                        <p className="text-sm text-slate-500 dark:text-slate-300">
                          {t('connections.partner.contactEmail')} {client.technicalResponsibleEmail || client.email}
                        </p>
                        <p className="text-sm text-slate-500 dark:text-slate-300 mt-1">
                          {client.portalDomain || t('connections.partner.noMainDomain')}
                        </p>
                        <div className="text-xs text-slate-500 dark:text-slate-300 mt-2 flex gap-3">
                          <span>{t('connections.partner.plan')} {client.subscriptionPlan}</span>
                          <span>{t('connections.partner.stripeStatus')} {client.stripeSubscriptionStatus || t('connections.partner.na')}</span>
                          <span>{t('connections.partner.botLimit')} {client.botLimit ?? t('connections.partner.na')}</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 justify-end shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenClientReadOnly(client.id)}
                          disabled={openingClientId === client.id}
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          {openingClientId === client.id ? t('connections.partner.enteringReadOnly') : t('connections.partner.enterReadOnly')}
                        </Button>
                        <ClientActionsMenu
                          label={t('connections.partner.manage')}
                          actions={[
                            {
                              key: 'plan',
                              label: t('connections.partner.managePlan'),
                              icon: <SlidersHorizontal className="w-4 h-4" />,
                              onClick: () => {
                                setManagePlanBotLimit(client.botLimit ?? 1);
                                setManagePlanStatus(client.status === 'suspended' ? 'suspended' : 'active');
                                setManagePlanClient(client);
                              },
                            },
                            { key: 'divider-1', divider: true },
                            {
                              key: 'contract',
                              label: contractingLicenseClientId === client.id
                                ? t('connections.partner.openingCheckout')
                                : t('connections.partner.contractLicense'),
                              icon: <CreditCard className="w-4 h-4" />,
                              onClick: () => openLicensePlanModal(client),
                              disabled: contractingLicenseClientId === client.id || ['active', 'trialing'].includes((client.stripeSubscriptionStatus || '').toLowerCase()),
                              title: ['active', 'trialing'].includes((client.stripeSubscriptionStatus || '').toLowerCase())
                                ? t('connections.partner.customerAlreadyLicensed')
                                : t('connections.partner.openStripeCheckout'),
                            },
                            {
                              key: 'payment',
                              label: managingPaymentClientId === client.id
                                ? t('connections.partner.openingPortal')
                                : t('connections.partner.managePayment'),
                              icon: <CreditCard className="w-4 h-4" />,
                              onClick: () => handleManagePayment(client.id),
                              disabled: managingPaymentClientId === client.id,
                              title: t('connections.partner.managePaymentHint'),
                            },
                            {
                              key: 'cancel',
                              label: cancellingLicenseClientId === client.id
                                ? t('connections.partner.openingPortal')
                                : t('connections.partner.cancelLicense'),
                              icon: <XCircle className="w-4 h-4" />,
                              onClick: () => handleCancelLicense(client.id),
                              disabled: cancellingLicenseClientId === client.id || !canCancelLicense,
                              title: canCancelLicense
                                ? t('connections.partner.openStripePortal')
                                : t('connections.partner.noLicenseToCancel'),
                            },
                            { key: 'divider-2', divider: true },
                            {
                              key: 'contact',
                              label: updatingContactClientId === client.id
                                ? t('connections.partner.updatingContact')
                                : t('connections.partner.updateContact'),
                              icon: <Mail className="w-4 h-4" />,
                              onClick: () => handleUpdateClientContactEmail(client),
                              disabled: updatingContactClientId === client.id,
                            },
                            {
                              key: 'unlink',
                              label: unlinkingClientId === client.id
                                ? t('connections.partner.unlinking')
                                : t('connections.partner.unlink'),
                              icon: <Unlink className="w-4 h-4" />,
                              onClick: () => handleUnlinkClient(client.id),
                              disabled: unlinkingClientId === client.id,
                              danger: true,
                            },
                          ]}
                        />
                      </div>
                    </div>
                      );
                    })()}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {managePlanClient && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-md rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5 shadow-xl">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                Gerenciar Plano
              </h3>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-300">
                Defina diretamente o plano e o limite de bots para <strong>{managePlanClient.name}</strong>.
              </p>

              <div className="mt-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                    Status do cliente
                  </label>
                  <select
                    value={managePlanStatus}
                    onChange={(e) => setManagePlanStatus(e.target.value as 'active' | 'suspended')}
                    className="w-full h-10 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 text-sm text-slate-700 dark:text-slate-200"
                    disabled={savingPlan}
                  >
                    <option value="active">Ativo</option>
                    <option value="suspended">Suspenso</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                    Limite de bots
                  </label>
                  <select
                    value={managePlanBotLimit}
                    onChange={(e) => setManagePlanBotLimit(Number(e.target.value))}
                    className="w-full h-10 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 text-sm text-slate-700 dark:text-slate-200"
                    disabled={savingPlan}
                  >
                    {[1, 2, 3, 4, 5].map((n) => (
                      <option key={n} value={n}>{n} bot{n > 1 ? 's' : ''}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                    1 bot = Starter · 2+ bots = Professional
                  </p>
                </div>
              </div>

              <div className="mt-5 flex items-center justify-end gap-2">
                <Button variant="outline" onClick={() => setManagePlanClient(null)} disabled={savingPlan}>
                  Cancelar
                </Button>
                <Button
                  isLoading={savingPlan}
                  onClick={async () => {
                    if (!tenant?.id || !managePlanClient) return;
                    setSavingPlan(true);
                    try {
                      await api.setPartnerClientPlan(tenant.id, managePlanClient.id, managePlanBotLimit, managePlanStatus);
                      toast.success('Plano atualizado com sucesso!');
                      setManagePlanClient(null);
                      await loadPartnerClients();
                    } catch (error: any) {
                      toast.error(error?.message || 'Erro ao atualizar plano.');
                    } finally {
                      setSavingPlan(false);
                    }
                  }}
                >
                  Salvar
                </Button>
              </div>
            </div>
          </div>
        )}

        {licenseModalClient && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-md rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5 shadow-xl">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                {t('connections.partner.selectLicensePlanModalTitle')}
              </h3>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-300">
                {t('connections.partner.selectLicensePlanModalDescription', { client: licenseModalClient.name })}
              </p>

              <div className="mt-4">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">
                  {t('connections.partner.selectLicensePlan')}
                </label>
                <select
                  value={licenseModalPlanCode}
                  onChange={(event) => setLicenseModalPlanCode(event.target.value)}
                  className="w-full h-10 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 text-sm text-slate-700 dark:text-slate-200"
                >
                  {getAvailablePlansForClient(licenseModalClient).map((plan) => (
                    <option key={plan.code} value={plan.code}>
                      {plan.name} ({plan.bots} bots)
                    </option>
                  ))}
                </select>
              </div>

              <div className="mt-5 flex items-center justify-end gap-2">
                <Button variant="outline" onClick={closeLicensePlanModal} disabled={!!contractingLicenseClientId}>
                  {t('connections.partner.modalCancel')}
                </Button>
                <Button onClick={() => void confirmLicensePlanModal()} isLoading={!!contractingLicenseClientId}>
                  {t('connections.partner.modalConfirmPurchase')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.24, ease: 'easeOut' }}
      className="space-y-6"
    >
      {isPartnerActingAsClient && (
        <Card className="bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/25">
          <CardContent className="p-4 flex items-center justify-between gap-3">
            <p className="text-sm text-amber-900 dark:text-amber-200">
              {t('connections.partner.actingAsClient')}
            </p>
            <Button variant="outline" size="sm" onClick={handleBackToPartner}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('connections.partner.backToPartner')}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{t('connections.defaultView.title')}</h1>
          <p className="text-slate-500 dark:text-slate-300 mt-1">
            {t('connections.defaultView.subtitle')}
          </p>
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 dark:bg-blue-500/15 border-blue-200 dark:border-blue-500/30">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-500/20 rounded-lg">
              <Link2 className="w-5 h-5 text-blue-600 dark:text-blue-300" />
            </div>
            <div>
              <p className="font-medium text-blue-900 dark:text-blue-200">{t('connections.defaultView.infoTitle')}</p>
              <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                {t('connections.defaultView.infoDescription')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Connections List */}
      {connections.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Link2 className="w-16 h-16 text-slate-300 dark:text-slate-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
                {t('connections.defaultView.noConnectionsTitle')}
              </h3>
              <p className="text-slate-500 dark:text-slate-300 mb-4 max-w-md mx-auto">
                {t('connections.defaultView.noConnectionsDescription')}
              </p>
              <a
                href="https://www.bitrix24.com/apps/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  {t('connections.defaultView.marketplaceButton')}
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {connections.map((connection, index) => (
            <motion.div
              key={connection.id}
              initial={{ opacity: 0, y: 12, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.24, delay: index * 0.04, ease: 'easeOut' }}
            >
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Link2 className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-3">
                          <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                            {connection.portalInfo?.portalName || connection.portalDomain}
                          </h3>
                          {getStatusBadge(connection.status)}
                        </div>
                        <p className="text-sm text-slate-500 dark:text-slate-300 mt-1">
                          {connection.portalDomain}
                        </p>
                        {connection.lastError && (
                          <p className="text-sm text-red-600 mt-2 flex items-center gap-1">
                            <AlertCircle className="w-4 h-4" />
                            {t('connections.defaultView.lastErrorPrefix')} {connection.lastError}
                          </p>
                        )}
                        <div className="flex items-center gap-4 mt-3 text-sm text-slate-500 dark:text-slate-300">
                          <span className="flex items-center gap-1">
                            <Bot className="w-4 h-4" />
                            {t('connections.defaultView.botsCount', {
                              count: connection.registeredBots?.length || 0,
                            })}
                          </span>
                          <span>
                            {t('connections.defaultView.connectedAt', {
                              date: new Date(connection.createdAt).toLocaleDateString(currentLocale),
                            })}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {(connection.status === 'error' || !!connection.lastError) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleReconnect(connection.id)}
                          disabled={reconnecting === connection.id}
                        >
                          <RefreshCw
                            className={`w-4 h-4 mr-2 ${
                              reconnecting === connection.id ? 'animate-spin' : ''
                            }`}
                          />
                          {t('connections.defaultView.fixConnection')}
                        </Button>
                      )}
                      {isOAuthRelatedError(connection) && (
                        <a
                          href={getRenewAppUrl(connection.portalDomain)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button variant="outline" size="sm">
                            <ExternalLink className="w-4 h-4 mr-2" />
                            {t('connections.defaultView.renewApp')}
                          </Button>
                        </a>
                      )}
                      <a
                        href={`https://${connection.portalDomain}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button variant="ghost" size="sm">
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </a>
                    </div>
                  </div>

                  {/* Bots list */}
                  {connection.registeredBots && connection.registeredBots.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-700">
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">
                        {t('connections.defaultView.botsInConnection')}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {connection.registeredBots.map((bot) => (
                          <Badge
                            key={bot.id}
                            variant={bot.status === 'active' ? 'success' : 'default'}
                          >
                            {bot.name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}
    </motion.div>
  );
}
